import React from "react";
import FormRow from "../../HorizontalForm/FormRow";
import FormRowSplit from "../../HorizontalForm/FormRowSplit";
import FormRowSplitLabelled from "../../HorizontalForm/FormRowSplitLabelled";
import TextField from "@mui/material/TextField";
import {DateTime} from "luxon";
import { DateFormat, dateFromState } from "../../../Helpers/DateTimeHelper";
import SelectInput from "../../Inputs/SelectInput";
import {getError, getErrorsArray, getErrorsFor} from "../../../Helpers/ErrorHelper";
import DaysOfWeekInput from "../../Inputs/DaysOfWeekInput";
import PropTypes from "prop-types";
import { intervalFromState } from "../../../Helpers/IntervalHelper";
import { format } from "date-fns";

const recurrenceRepeatOptions = [
    {value: 'daily', label: 'Daily'},
    {value: 'weekly', label: 'Weekly'},
    {value: 'monthly', label: 'Monthly'},
    {value: 'yearly', label: 'Yearly'},
]

const OnTypeOptions = [
    {value: 'day', label: 'Day'},
    {value: '1st', label: 'First'},
    {value: '2nd', label: 'Second'},
    {value: '3rd', label: 'Third'},
    {value: '4th', label: 'Fourth'},
    {value: 'ld', label: 'Last Day'},
    {value: 'lwd', label: 'Last Weekday'},
    {value: 'lwed', label: 'Last Weekend Day'},
];

const daysOfWeek = [
    {value: 1, label: 'Monday'},
    {value: 2, label: 'Tuesday'},
    {value: 3, label: 'Wednesday'},
    {value: 4, label: 'Thursday'},
    {value: 5, label: 'Friday'},
    {value: 6, label: 'Saturday'},
    {value: 7, label: 'Sunday'},
];

const monthsOfYear = [
    {value: 1, label: 'January'},
    {value: 2, label: 'February'},
    {value: 3, label: 'March'},
    {value: 4, label: 'April'},
    {value: 5, label: 'May'},
    {value: 6, label: 'June'},
    {value: 7, label: 'July'},
    {value: 8, label: 'August'},
    {value: 9, label: 'September'},
    {value: 10, label: 'October'},
    {value: 11, label: 'November'},
    {value: 12, label: 'December'}
];

const daysInCalenderMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

const RecurrenceForm = (props) => {

    const getEveryText = () => {
        switch (props.recurrence.repeats) {
            case 'daily':
                return 'Day(s)';
            case 'weekly':
                return 'Week(s)';
            case 'monthly':
                return 'Month(s)';
            default:
                return '';
        }
    }

    const getDaysOfMonthOptions = () => {
        let options = [];

        let daysInMonth = (props.recurrence.repeats === 'yearly' && props.recurrence.every)
            ? daysInCalenderMonth[props.recurrence.every - 1]
            : 31;

        for (let day = 1; day <= daysInMonth; day++) {
            options.push({value: day, label: day});
        }
        return options;
    }

    const repeats = (repeatTypes) => {
        return (repeatTypes.indexOf(props.recurrence.repeats) !== -1);
    }

    const hasOnType = (onTypes) => {
        return (onTypes.indexOf(props.recurrence.on_type) !== -1);
    }

    const getFixedStartDateText = () => {
        if (!props.fixedTimeslot || !props.fixedTimeslot.start.length) {
            return '';
        }
        let interval = intervalFromState(props.fixedTimeslot)
        return format(interval.start, DateFormat.HUMAN_SLASHED_DATE);
    }

    const getFixedTimeslotText = () => {
        if (!props.fixedTimeslot || !props.fixedTimeslot.start.length) {
            return <span className="alert">Please choose a date from the main booking form</span>
        }

        let interval = intervalFromState(props.fixedTimeslot)
        return format(interval.start, DateFormat.MYSQL_TIME_WITHOUT_SECONDS) + " - " + format(interval.end, DateFormat.MYSQL_TIME_WITHOUT_SECONDS);
    }

    const handleChange = (name, value) => {
        let toChange = {
            [name]: value
        };

        // If we're changing repeats to / from 'weekly' clear the 'on' value
        if ((name === 'repeats') && (value === 'weekly' || props.recurrence.repeats === 'weekly')) {
            toChange['on'] = (props.recurrence.on_type === 'day') ? '' : null;
        }

        if ((name === 'on_type') && (props.recurrence.on_type === 'day' || value === 'day')) {
            toChange['on'] = (value === 'day') ? '' : null;
        }

        props.onRecurrenceChange(toChange);
    }

    const handleRepeatsChange = (value) => {
        handleChange('repeats', value);
    }

    /**
     * We may optionally want some fields to update onBlur rather than
     * onChange.
     * A current example of this is the bookings RecurrenceManagementForm
     * which handles the state slightly differently, and triggers an api
     * request whenever the date changes. If the date picker is set to onChange,
     * this will trigger the api call whenever the user flicks to another month
     * in the datepicker.
     * @param name
     * @param value
     */
    const handleUpdateOnBlur = (name, value) => {
        if (props.useOnBlurUpdates) {
            handleChange(name, value);
        }
    }

    const handleUpdateOnChange = (name, value) => {
        if (!props.useOnBlurUpdates) {
            handleChange(name, value);
        }
    }

    const handleEndDateChange = (value) => {
        handleChange('end_date', value);
    }

    return (
        <form className="recurring-datetime-form">
            <FormRow
                label="Repeats"
                required
                errorMessage={getError(props.errors, 'repeats', props.errorPrefix)}
            >
                <SelectInput
                    name="repeats"
                    className="input-narrow"
                    options={recurrenceRepeatOptions}
                    value={props.recurrence.repeats}
                    onChange={(e) => handleRepeatsChange(e.target.value)}
                    styles={{container: {width: '50%'}}}
                />
            </FormRow>

            { repeats(['daily', 'weekly', 'monthly']) && (
                <FormRow
                    label="Every"
                    required
                    errorMessage={getError(props.errors, 'every', props.errorPrefix)}
                >
                    <div className="grid-x grid-margin-x">
                        <div className="cell small-4">
                            <input
                                type="number"
                                name="every"
                                value={props.recurrence.every}
                                min="1"
                                onChange={(e) => handleChange(e.target.name, e.target.value)}
                            />
                        </div>
                        <div className="cell small-8 field-postfix">
                            <span>
                                {getEveryText()}
                            </span>
                        </div>
                    </div>
                </FormRow>
            )}

            { repeats(['yearly']) && (
                <FormRow
                    label="Every"
                    required
                    errorMessage={getError(props.errors, 'every', props.errorPrefix)}
                >
                    <div className="grid-x grid-margin-x">
                        <div className="cell small-7">
                            <SelectInput name="every"
                                options={monthsOfYear}
                                value={props.recurrence.every}
                                onChange={(e) => handleChange(e.target.name, e.target.value)}
                            />
                        </div>
                    </div>
                </FormRow>
            )}

            { repeats(['weekly']) && (
                <FormRow
                    label="On"
                    required
                    errorMessage={getError(props.errors, 'on', props.errorPrefix)}
                >
                    <div className="grid-x grid-margin-x">
                        <div className="cell small-12">
                            <DaysOfWeekInput
                                value={props.recurrence.on}
                                onChange={(value) => handleChange('on', value)}
                            />
                        </div>
                    </div>
                </FormRow>
            )}

            { repeats(['monthly','yearly']) && (
                <FormRowSplitLabelled
                    label="On"
                    errorMessages={getErrorsArray(props.errors, ['on_type', 'on'], props.errorPrefix)}
                    required
                >
                    <SelectInput
                        name="on_type"
                        options={OnTypeOptions}
                        value={props.recurrence.on_type}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                    />
                    <>
                        { hasOnType(['1st', '2nd', '3rd', '4th', 'last']) && (
                            <SelectInput
                                name="on"
                                options={daysOfWeek}
                                value={props.recurrence.on}
                                onChange={(e) => handleChange(e.target.name, e.target.value)}
                            />
                        )}
                        { hasOnType(['day']) && (
                            <SelectInput
                                name="on"
                                options={getDaysOfMonthOptions()}
                                value={props.recurrence.on}
                                onChange={(e) => handleChange(e.target.name, e.target.value)}
                            />
                        )}
                    </>
                </FormRowSplitLabelled>
            )}

            {(!props.allowDateTimeChange) && (<>
                <FormRow label="Start Date">
                    <span>
                        { getFixedStartDateText() }
                    </span>
                </FormRow>

                <FormRow label="Timeslot">
                    <span>
                        { getFixedTimeslotText() }
                    </span>
                </FormRow>
            </>)}

            {(props.allowDateTimeChange) && (
                <FormRowSplitLabelled
                    label="Start Date"
                    errorMessages={getErrorsArray(props.errors, ['start_date', 'time'], props.errorPrefix)}
                    required
                >
                    <TextField
                        type="date"
                        name="start_date"
                        value={props.recurrence.start_date}
                        onChange={(e) => handleUpdateOnChange(e.target.name, e.target.value)}
                        onBlur={(e) => handleUpdateOnBlur(e.target.name, e.target.value)}
                        InputLabelProps={{shrink: true,}}
                        inputProps={{
                            min: DateTime.local().toFormat(DateFormat.MYSQL_DATE)
                        }}
                    />
                    <TextField
                        type="time"
                        name="time"
                        value={props.recurrence.time}
                        onChange={(e) => handleUpdateOnChange(e.target.name, e.target.value)}
                        onBlur={(e) => handleUpdateOnBlur(e.target.name, e.target.value)}
                        InputLabelProps={{shrink: true,}}
                    />
                </FormRowSplitLabelled>
            )}

            <FormRowSplitLabelled
                label="End Date"
                errorMessages={getErrorsArray(props.errors, ['end_date', 'end_occurrences'], props.errorPrefix)}
                required
            >
                <TextField
                    type="date"
                    name="end_date"
                    value={props.recurrence.end_date}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    InputLabelProps={{shrink: true,}}
                    inputProps={{
                        min: (props.recurrence.start_date)
                            ? DateTime.fromFormat(props.recurrence.start_date, DateFormat.MYSQL_DATE).plus({days: 1}).toFormat(DateFormat.MYSQL_DATE)
                            : DateTime.local().plus({days: 1}).toFormat(DateFormat.MYSQL_DATE),
                        max: (props.maxEndDate)
                            ? format(dateFromState(props.maxEndDate), DateFormat.MYSQL_DATE)
                            : DateTime.local().plus({years: 1}).toFormat(DateFormat.MYSQL_DATE)
                    }}
                />
                <span></span>
            </FormRowSplitLabelled>
        </form>
    )
}

RecurrenceForm.propTypes = {
    allowDateTimeChange: PropTypes.bool,
    fixedTimeslot: PropTypes.object,
    useOnBlurUpdates: PropTypes.bool,
}

RecurrenceForm.defaultProps = {
    allowDateTimeChange: true,
    useOnBlurUpdates: false,
}

export default RecurrenceForm;
